import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  apiList,
  RCS_SERVICE_INDEX,
  SMS_RCS_SERVICE_INDEX,
  SMS_SERVICE_INDEX,
} from "../../../helpers/apiList";
import { toast } from "react-toastify";
import CustomAlert from "../../../helpers/CustomAlert";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import confetti from "canvas-confetti";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const UploadDoc = ({
  payloadData,
  setPayloadData,
  loggedInUserName,
  handleNext,
  handleBack,
  CloseCreate,
  fetchCustomerData,
  action,
  editData,
  documents,
  setDocuments,
  initialDocuments,
}) => {
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [isFromUrl, setIsFromUrl] = useState(false); // Track if preview is from URL
  const [missingDocs, setMissingDocs] = useState([]);
  const [uploadErrors, setUploadErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Function to convert URL to base64 for PDF preview
  const convertUrlToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error converting URL to base64:", error);
      return null;
    }
  };

  useEffect(() => {
    if (action !== "add" && editData) {
      setDocuments((prev) =>
        prev.map((doc) => {
          const imageUrl = editData[doc.editDataKey];
          if (imageUrl) {
            // Determine file type from URL extension or default to PDF
            let fileType = "application/pdf";
            const urlLower = imageUrl.toLowerCase();
            if (urlLower.includes(".jpg") || urlLower.includes(".jpeg")) {
              fileType = "image/jpeg";
            } else if (urlLower.includes(".png")) {
              fileType = "image/png";
            } else if (urlLower.includes(".gif")) {
              fileType = "image/gif";
            } else if (urlLower.includes(".pdf")) {
              fileType = "application/pdf";
            }

            // Extract filename from URL or use document name
            const urlParts = imageUrl.split("/");
            const fileName =
              urlParts[urlParts.length - 1] ||
              `${doc.name}.${fileType.includes("image") ? "jpg" : "pdf"}`;

            // Fetch file size
            fetch(imageUrl, { method: "HEAD" })
              .then((response) => {
                const contentLength = response.headers.get("Content-Length");
                if (contentLength) {
                  const fileSizeBytes = parseInt(contentLength);
                  const fileSizeKB = (fileSizeBytes / 1024).toFixed(2);

                  // Update the document with the actual file size
                  setDocuments((prevDocs) =>
                    prevDocs.map((prevDoc) =>
                      prevDoc.id === doc.id
                        ? { ...prevDoc, fileSize: `${fileSizeKB} KB` }
                        : prevDoc
                    )
                  );
                }
              })
              .catch((error) => {
                console.log(
                  `Could not fetch file size for ${doc.name}:`,
                  error
                );
                // Keep the default "Loading..." size
              });

            return {
              ...doc,
              uploaded: true,
              fileBase64: imageUrl, // Store the direct URL
              fileName: fileName,
              fileType: fileType,
              fileSize: "Loading...", // Initial placeholder while fetching size
              isFromUrl: true, // Mark as URL-based file
            };
          }
          return doc;
        })
      );
    }
  }, [action, editData]);

  const handleUpload = (event, docId) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
      ];
      const MAX_SIZE_KB = 1024; // 1 MB

      setUploadErrors((prev) => ({ ...prev, [docId]: "" }));

      if (!validTypes.includes(file.type)) {
        setUploadErrors((prev) => ({
          ...prev,
          [docId]: "Only image and PDF files are allowed.",
        }));
        return;
      }

      const fileSizeKB = file.size / 1024;
      if (fileSizeKB > MAX_SIZE_KB) {
        setUploadErrors((prev) => ({
          ...prev,
          [docId]: "File size should not exceed 1 MB.",
        }));
        return;
      }

      const docMeta = initialDocuments.find((doc) => doc.id === docId);
      const key = docMeta?.editDataKey?.replace("FilePath", ""); // Convert to matching payload key

      if (!key) return;

      // Store actual File object in payloadData for backend
      setPayloadData((prev) => ({
        ...prev,
        [key]: file,
      }));

      // Generate base64 for UI preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;

        // Update documents state for UI display
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === docId
              ? {
                  ...doc,
                  uploaded: true,
                  fileSize: fileSizeKB.toFixed(2) + " KB",
                  fileName: file.name,
                  fileType: file.type,
                  fileBase64: base64String, // Store base64 for preview
                  isFromUrl: false,
                }
              : doc
          )
        );

        setMissingDocs((prev) => prev.filter((id) => id !== docId));
      };

      reader.readAsDataURL(file); // Convert file to base64
    }
  };

  const handleDelete = (docId) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === docId
          ? {
              ...doc,
              uploaded: false,
              fileBase64: null,
              fileSize: null,
              fileName: null,
              fileType: null,
              isFromUrl: false,
            }
          : doc
      )
    );

    setPayloadData((prev) => ({
      ...prev,
      documents: (prev.documents || []).filter((doc) => doc.id !== docId),
    }));

    setUploadErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[docId];
      return newErrors;
    });
  };

  const handlePreview = async (doc) => {
    if (doc.fileBase64) {
      setPreviewName(doc.name);
      setPreviewType(doc.fileType || "");
      setIsFromUrl(doc.isFromUrl || false);

      // If it's a PDF from URL, try to convert it to base64 for proper preview
      if (doc.isFromUrl && doc.fileType === "application/pdf") {
        setLoading(true);
        try {
          const base64Data = await convertUrlToBase64(doc.fileBase64);
          setLoading(false);
          if (base64Data) {
            setPreviewDoc(base64Data);
          } else {
            // Keep original URL and let renderPreviewContent handle the fallback
            setPreviewDoc(doc.fileBase64);
          }
        } catch (error) {
          console.error("Error converting PDF:", error);
          setLoading(false);
          // Keep original URL for fallback handling
          setPreviewDoc(doc.fileBase64);
        }
      } else {
        // For images or base64 PDFs, use directly
        setPreviewDoc(doc.fileBase64);
      }
    }
  };

  const handleClosePreview = () => {
    setPreviewDoc(null);
    setPreviewName("");
    setPreviewType("");
    setIsFromUrl(false);
  };

  const validateAndProceed = (callback) => {
    const missing = documents
      .filter((doc) => !doc.uploaded)
      .map((doc) => doc.id);
    if (missing.length > 0) {
      setMissingDocs(missing);
    } else {
      setMissingDocs([]);
      callback();
    }
  };

  const renderPreviewContent = () => {
    if (previewType.includes("pdf")) {
      // Check if it's a URL-based PDF that might not display in iframe
      const isPdfUrl = isFromUrl && !previewDoc.startsWith("data:");

      return (
        <div>
          {isPdfUrl ? (
            // For PDF URLs that might not work in iframe, show alternative options
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div
                style={{
                  marginBottom: "20px",
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                PDF preview may not be available due to server restrictions.
              </div>

              {/* Still try to show in iframe as fallback */}
              <div style={{ marginTop: "20px", border: "1px solid #ddd" }}>
                <iframe
                  src={previewDoc}
                  width="100%"
                  height="400px"
                  title="PDF Preview"
                  style={{ border: "none" }}
                  onError={() => console.log("PDF iframe failed to load")}
                />
              </div>
            </div>
          ) : (
            // For base64 PDFs or when conversion was successful
            <div>
              <iframe
                src={previewDoc}
                width="100%"
                height="500px"
                title="PDF Preview"
                style={{ border: "none" }}
              />
            </div>
          )}
        </div>
      );
    } else if (previewType.includes("image")) {
      return (
        <div>
          <img
            src={previewDoc}
            alt="Document Preview"
            style={{ width: "100%", maxHeight: "500px", objectFit: "contain" }}
          />
        </div>
      );
    } else {
      return (
        <div>
          <p>Unsupported file format for preview.</p>
        </div>
      );
    }
  };

  const handleAddCustomer = () => {
    setLoading(true);

    const formData = new FormData();

    // Destructure file fields out
    const {
      gstCertificate,
      incorporationCertificate,
      aadharFront,
      aadharBack,
      panTan,
      authPersonPan,
      boardResolution,
      ...restFields // These will go into "request"
    } = payloadData;

    // Convert WhatsApp rates from Paise to Credits (multiply by 10) before sending
    if (restFields.waMarketingRate) {
      restFields.waMarketingRate = Number(restFields.waMarketingRate) * 10;
    }
    if (restFields.waUtilityRate) {
      restFields.waUtilityRate = Number(restFields.waUtilityRate) * 10;
    }
    if (restFields.waAuthenticationRate) {
      restFields.waAuthenticationRate = Number(restFields.waAuthenticationRate) * 10;
    }

    // Add indexNumbers inside the JSON request
    restFields.indexNumbers =
      restFields?.smsServices && !restFields?.rcsServices
        ? SMS_SERVICE_INDEX
        : restFields?.rcsServices && !restFields?.smsServices
        ? RCS_SERVICE_INDEX
        : restFields?.smsServices && restFields?.rcsServices
        ? SMS_RCS_SERVICE_INDEX
        : "";

    // Remove file fields from JSON request
    formData.append("request", JSON.stringify(restFields));

    // Append each file separately — only if it exists
    if (gstCertificate) formData.append("gstCertificate", gstCertificate);
    if (incorporationCertificate)
      formData.append("incorporationCertificate", incorporationCertificate);
    if (aadharFront) formData.append("aadharFront", aadharFront);
    if (aadharBack) formData.append("aadharBack", aadharBack);
    if (panTan) formData.append("panTan", panTan);
    if (authPersonPan) formData.append("authPersonPan", authPersonPan);
    if (boardResolution) formData.append("boardResolution", boardResolution);

    // Choose API endpoint
    let apiUrl = "";
    let successMessage = "";

    if (action === "add") {
      apiUrl = apiList.CREATE_CUSTOMER_DATA;
      successMessage = "Customer added successfully";
    } else if (action === "modify") {
      apiUrl = apiList.MODIFY_CUSTOMER_DATA;
      successMessage = "Customer details modified successfully";
    }

    // API call
    axiosPostApi(apiUrl, formData)
      .then((response) => {
        setLoading(false);
        if (response.status === 200 && response.data.status === true) {
          CustomAlert({
            title: "Success",
            text: response?.data?.message || successMessage,
            icon: "success",
            confirmButtonText: "Ok",
          });

          // 🎉 Confetti effect
          const canvas = document.createElement("canvas");
          canvas.style.position = "fixed";
          canvas.style.top = "0";
          canvas.style.left = "0";
          canvas.style.width = "100%";
          canvas.style.height = "100%";
          canvas.style.zIndex = "3000";
          document.body.appendChild(canvas);

          const confettiInstance = confetti.create(canvas, {
            resize: true,
            useWorker: true,
          });

          confettiInstance({
            particleCount: 250,
            spread: 100,
            origin: { y: 0.6 },
          });

          setTimeout(() => {
            document.body.removeChild(canvas);
          }, 3000);

          CloseCreate();
          fetchCustomerData(1);
        } else {
          CustomAlert({
            title: "Failed",
            text:
              response?.data?.message ||
              "There was an issue processing the request.",
            icon: "error",
            confirmButtonText: "Retry",
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error:", error);
        CustomAlert({
          title: "Error",
          text: "An error occurred while processing.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      });
  };

  const handleAuthorize = () => {
    setIsAuthModalOpen(true);
  };

  const handleAuthSubmit = async (action, remarks) => {
    let status;
    let actionText;

    // Map the action to the correct status code
    switch (action) {
      case "approve":
        status = "1";
        actionText = "authorized";
        break;
      case "block":
        status = "3";
        actionText = "blocked";
        break;
      case "reject":
        status = "5";
        actionText = "rejected";
        break;
      default:
        status = "5"; // Default to reject if action is invalid
        actionText = "rejected";
    }

    const requestBody = {
      username: loggedInUserName,
      customerCode: payloadData?.customerCode || "",
      status: status,
      adminRemark: remarks || "",
    };

    try {
      const response = await axiosPostApi(
        apiList.AUTH_CUSTOMER_DATA,
        requestBody
      );
      if (response?.status === 200 && response?.data?.respCode === "00") {
        CustomAlert({
          title: "Success",
          text: `Company ${actionText} successfully!`,
          icon: "success",
          confirmButtonText: "Ok",
        });

        // Show confetti only for approval
        if (action === "approve") {
          const canvas = document.createElement("canvas");
          canvas.style.position = "fixed";
          canvas.style.top = "0";
          canvas.style.left = "0";
          canvas.style.width = "100%";
          canvas.style.height = "100%";
          canvas.style.zIndex = "3000";
          document.body.appendChild(canvas);

          const confettiInstance = confetti.create(canvas, {
            resize: true,
            useWorker: true,
          });

          confettiInstance({
            particleCount: 250,
            spread: 100,
            origin: { y: 0.6 },
          });

          setTimeout(() => {
            document.body.removeChild(canvas);
          }, 3000);
        }

        fetchCustomerData(1);
        CloseCreate();
      } else {
        CustomAlert({
          title: `Failed to ${actionText} customer`,
          text:
            response.data?.message || "Invalid credentials. Please try again!",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      console.error(`Error ${actionText} customer`, error);
      toast.error(`Failed to ${actionText} customer.`);
    }

    setIsAuthModalOpen(false);
    // setAuthData(null);
  };

  return (
    <>
      <form>
        <div className="p-4" style={{ fontFamily: "'Roboto', sans-serif" }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: "#00BBD3" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff" }}>No.</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Document Name</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Max Limit</TableCell>
                  <TableCell
                    sx={{
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Action Required
                    <Tooltip
                      title="Supported Formats: PDF and Image (JPG, JPEG, PNG)"
                      arrow
                    >
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{
                          ml: 1,
                          cursor: "pointer",
                          color: "#fff",
                          opacity: 0.8,
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc, index) => (
                  <TableRow key={doc.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{doc.name}</TableCell>
                    <TableCell>
                      <InsertDriveFileIcon
                        sx={{ verticalAlign: "middle", mr: 1 }}
                      />
                      1 MB
                    </TableCell>
                    <TableCell>
                      {action === "view" ||
                      action === "delete" ||
                      action === "auth" ? (
                        doc.uploaded ? (
                          <IconButton
                            color="primary"
                            onClick={() => handlePreview(doc)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        ) : (
                          <span style={{ color: "#999", fontStyle: "italic" }}>
                            No document uploaded
                          </span>
                        )
                      ) : doc.uploaded ? (
                        <>
                          <IconButton
                            color="primary"
                            onClick={() => handlePreview(doc)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(doc.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <input
                            accept="image/*,.pdf"
                            type="file"
                            id={`upload-${doc.id}`}
                            style={{ display: "none" }}
                            onChange={(e) => handleUpload(e, doc.id)}
                          />
                          <label htmlFor={`upload-${doc.id}`}>
                            <Button
                              variant="contained"
                              component="span"
                              startIcon={<UploadIcon />}
                              sx={{
                                backgroundColor: "#00BBD3",
                                "&:hover": { backgroundColor: "#009CAD" },
                              }}
                            >
                              Upload Document
                            </Button>
                          </label>
                          {(uploadErrors[doc.id] ||
                            missingDocs.includes(doc.id)) && (
                            <div
                              style={{
                                color: "red",
                                fontSize: "0.85rem",
                                marginTop: "4px",
                              }}
                            >
                              {uploadErrors[doc.id]
                                ? uploadErrors[doc.id]
                                : `${doc.name} is required.`}
                            </div>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog
            open={Boolean(previewDoc)}
            onClose={handleClosePreview}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>{previewName}</div>
                <IconButton onClick={handleClosePreview}>
                  <HighlightOffIcon />
                </IconButton>
              </div>
            </DialogTitle>
            <DialogContent dividers>
              {loading ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <CircularProgress />
                  <p>Loading document preview...</p>
                </div>
              ) : (
                renderPreviewContent()
              )}
            </DialogContent>
          </Dialog>

          <div className="scoped-modal modal-footer mt-4 justify-center">
            <button
              type="button"
              className="btn btn-back !rounded-lg w-[120px]"
              onClick={handleBack}
            >
              Back
            </button>

            {action === "view" ? (
              <button
                type="button"
                onClick={CloseCreate}
                className="btn btn-submit !rounded-lg w-[120px]"
              >
                Close
              </button>
            ) : action === "delete" ? (
              <button
                type="button"
                onClick={openModal}
                className="btn btn-danger !rounded-lg w-[120px]"
              >
                Delete
              </button>
            ) : action === "auth" ? (
              <button
                type="button"
                onClick={() => handleAuthorize()}
                className="btn btn-back !rounded-lg w-[120px]"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={() => validateAndProceed(handleAddCustomer)}
                className="btn btn-submit !rounded-lg w-[120px]"
              >
                {loading ? (
                  <CircularProgress style={{ width: "20px", height: "20px" }} />
                ) : action === "modify" ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </button>
            )}
          </div>
        </div>
      </form>
      <DeleteConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        editData={editData}
        fetchCustomerData={fetchCustomerData}
        CloseCreate={CloseCreate}
      />
      {isAuthModalOpen && (
        <AuthorizationConfirmationModal
          show={isAuthModalOpen}
          handleClose={() => setIsAuthModalOpen(false)}
          handleSubmit={handleAuthSubmit}
        />
      )}
    </>
  );
};
const AuthorizationConfirmationModal = ({
  show,
  handleClose,
  handleSubmit,
}) => {
  const [action, setAction] = useState("approve");
  const [adminRemark, setRemarks] = useState("");
  const [error, setError] = useState("");

  if (!show) return null;

  const onSubmit = () => {
    if (!adminRemark.trim()) {
      setError("Remark is required");
      return;
    }
    setError("");
    handleSubmit(action, adminRemark);
  };

  const handleRemarkChange = (e) => {
    setRemarks(e.target.value);
    if (error && e.target.value.trim()) {
      setError("");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "50px", // Increased padding for more internal space
          borderRadius: "40px", // Increased for a more curved look
          width: "90%",
          maxWidth: "600px", // Increased size of the container
          textAlign: "center",
        }}
      >
        <h3 style={{ fontWeight: "bold", fontSize: "24px", color: "#1A202C" }}>
          Are you sure?
        </h3>
        <p
          style={{
            color: "#1A202C",
            fontSize: "18px",
            fontWeight: "normal",
            marginTop: "10px",
          }}
        >
          Do you want to authorize the company?
        </p>

        <div
          style={{
            margin: "20px 0",
            display: "flex",
            justifyContent: "center",
            gap: "40px",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "16px",
              color: "#1A202C",
            }}
          >
            <input
              type="radio"
              value="approve"
              checked={action === "approve"}
              onChange={() => setAction("approve")}
              style={{
                width: "20px",
                height: "20px",
                accentColor: "#00C4B4",
              }}
            />
            Approve
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "16px",
              color: "#1A202C",
            }}
          >
            <input
              type="radio"
              value="reject"
              checked={action === "reject"}
              onChange={() => setAction("reject")}
              style={{
                width: "20px",
                height: "20px",
                accentColor: action === "reject" ? "red" : "#00C4B4",
              }}
            />
            Reject
          </label>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              textAlign: "left",
              fontSize: "16px",
              color: "#1A202C",
            }}
          >
            Remark <span style={{ color: "red" }}>*</span>
          </label>
          <textarea
            placeholder="Enter your remarks here..."
            value={adminRemark}
            onChange={handleRemarkChange}
            style={{
              width: "100%",
              height: "100px",
              border: "1px solid #00C4B4",
              borderRadius: "10px",
              padding: "5px",
              boxSizing: "border-box",
              fontSize: "14px",
              color: "#A0AEC0",
            }}
          />
          {error && (
            <p
              style={{
                color: "red",
                fontSize: "14px",
                textAlign: "left",
                marginTop: "5px",
              }}
            >
              {error}
            </p>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <button
            onClick={handleClose}
            style={{
              padding: "10px",
              width: "120px",
              border: "1px solid #00C4B4",
              borderRadius: "20px",
              backgroundColor: "white",
              color: "#00C4B4",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            style={{
              padding: "10px",
              width: "120px",
              border: "none",
              borderRadius: "20px",
              backgroundColor: "#0dcaf0",
              color: "white",
              cursor: "pointer",
              fontSize: "16px",
              transition: "background-color 0.3s, color 0.3s, border 0.3s",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "white";
              e.target.style.color = "#0dcaf0";
              e.target.style.border = "1px solid #0dcaf0";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#0dcaf0";
              e.target.style.color = "white";
              e.target.style.border = "none";
            }}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDoc;
